import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  Users, Plus, Send, ArrowLeft, UserPlus, X, Check, Search, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Create Group Modal
function CreateGroupModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      apiClient.get('/group-chats/available-artists').then(r => setArtists(r.data)).catch(() => {});
    }
  }, [open]);

  const toggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Group name required'); return; }
    setLoading(true);
    try {
      await onCreate(name.trim(), selectedMembers);
      setName('');
      setSelectedMembers([]);
      onClose();
    } finally { setLoading(false); }
  };

  const filtered = artists.filter(a =>
    !search || (a.username || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg mx-4 rounded-2xl bg-gray-900 border border-white/10 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="create-group-modal">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Create Group Chat</h2>
          <Input
            data-testid="group-name-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Group name..."
            className="mt-3 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search artists..."
              className="pl-9 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.map(artist => (
            <div
              key={artist.user_id}
              onClick={() => toggleMember(artist.user_id)}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                selectedMembers.includes(artist.user_id) ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-white/5 hover:bg-white/10'
              }`}
              data-testid={`artist-option-${artist.user_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                {artist.profile_image ? (
                  <img src={artist.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30"><Users className="w-5 h-5" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{artist.username || 'Artist'}</p>
                <p className="text-white/40 text-xs truncate">{(artist.genres || []).join(', ')}</p>
              </div>
              {selectedMembers.includes(artist.user_id) && (
                <Check className="w-5 h-5 text-violet-400 flex-shrink-0" />
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-white/40 text-center py-8">No artists found</p>
          )}
        </div>
        <div className="p-4 border-t border-white/10 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20">Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} className="flex-1 bg-violet-600 hover:bg-violet-500" data-testid="create-group-btn">
            {loading ? 'Creating...' : `Create (${selectedMembers.length} members)`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Add Members Modal
function AddMembersModal({ open, onClose, groupId, currentMembers }) {
  const [artists, setArtists] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      apiClient.get('/group-chats/available-artists').then(r => {
        setArtists(r.data.filter(a => !currentMembers.includes(a.user_id)));
      }).catch(() => {});
    }
  }, [open, currentMembers]);

  const handleAdd = async () => {
    try {
      await apiClient.put(`/group-chats/${groupId}/members`, { member_ids: selected });
      toast.success('Members added!');
      onClose();
    } catch (e) {
      toast.error('Failed to add members');
    }
  };

  const filtered = artists.filter(a =>
    !search || (a.username || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg mx-4 rounded-2xl bg-gray-900 border border-white/10 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Add Members</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-white/40" /></button>
        </div>
        <div className="p-4">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-white/5 border-white/10 text-white" />
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.map(a => (
            <div key={a.user_id} onClick={() => setSelected(prev => prev.includes(a.user_id) ? prev.filter(x => x !== a.user_id) : [...prev, a.user_id])}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${selected.includes(a.user_id) ? 'bg-violet-500/20' : 'bg-white/5 hover:bg-white/10'}`}>
              <div className="w-8 h-8 rounded-full bg-white/10 overflow-hidden"><Users className="w-full h-full p-1.5 text-white/30" /></div>
              <span className="text-white flex-1">{a.username || 'Artist'}</span>
              {selected.includes(a.user_id) && <Check className="w-4 h-4 text-violet-400" />}
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-white/10">
          <Button onClick={handleAdd} disabled={!selected.length} className="w-full bg-violet-600">Add {selected.length} Members</Button>
        </div>
      </div>
    </div>
  );
}

export default function GroupChats() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { user } = useAuthStore();
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const messagesEndRef = useRef(null);
  const canManage = ['admin', 'owner', 'overseer'].includes(user?.role);

  useEffect(() => { fetchGroups(); }, []);
  useEffect(() => { if (groupId) fetchGroupChat(groupId); }, [groupId]);
  useEffect(() => {
    const box = messagesEndRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  const fetchGroups = async () => {
    try {
      const r = await apiClient.get('/group-chats');
      setGroups(r.data);
    } catch {}
  };

  const fetchGroupChat = async (id) => {
    try {
      const r = await apiClient.get(`/group-chats/${id}`);
      setActiveGroup(r.data.group);
      setMessages(r.data.messages);
      setMembers(r.data.members);
    } catch (e) {
      toast.error('Could not load group');
    }
  };

  const handleCreate = async (name, memberIds) => {
    const r = await apiClient.post('/group-chats', { name, member_ids: memberIds });
    toast.success('Group created!');
    fetchGroups();
    navigate(`/group-chats/${r.data.group_id}`);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !groupId) return;
    try {
      await apiClient.post(`/group-chats/${groupId}/messages`, { content: newMessage.trim() });
      setNewMessage('');
      fetchGroupChat(groupId);
    } catch { toast.error('Failed to send'); }
  };

  // Group list view
  if (!groupId) {
    return (
      <div className="min-h-screen relative pt-20">
        <div className="container mx-auto px-4 py-8 relative z-10 max-w-3xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold gradient-text">Group Chats</h1>
              <p className="text-white/50 mt-1">Connect with artists in dedicated groups</p>
            </div>
            {canManage && (
              <Button onClick={() => setShowCreate(true)} className="bg-violet-600 hover:bg-violet-500" data-testid="new-group-btn">
                <Plus className="w-4 h-4 mr-2" /> New Group
              </Button>
            )}
          </div>

          {groups.length === 0 ? (
            <Card className="glass-default border-white/10">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No group chats yet</p>
                {canManage && <Button onClick={() => setShowCreate(true)} className="mt-4 bg-violet-600">Create First Group</Button>}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {groups.map(group => (
                <Card
                  key={group.group_id}
                  className="glass-default border-white/10 hover:border-violet-500/30 cursor-pointer transition"
                  onClick={() => navigate(`/group-chats/${group.group_id}`)}
                  data-testid={`group-card-${group.group_id}`}
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">{group.name}</h3>
                      <p className="text-white/40 text-xs mt-0.5">{group.member_count} members</p>
                      {group.last_message && (
                        <p className="text-white/50 text-sm truncate mt-1">
                          <span className="text-violet-300">{group.last_message.sender_name}:</span> {group.last_message.content}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <CreateGroupModal open={showCreate} onClose={() => setShowCreate(false)} onCreate={handleCreate} />
        </div>
      </div>
    );
  }

  // Active group chat view
  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-4 relative z-10 max-w-3xl h-[calc(100vh-5rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 p-4 glass-default rounded-xl border border-white/10">
          <Button variant="ghost" size="sm" onClick={() => navigate('/group-chats')} className="text-white/60">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h2 className="font-bold text-white">{activeGroup?.name || 'Group Chat'}</h2>
            <p className="text-white/40 text-xs">{members.length} members</p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAddMembers(true)} className="bg-violet-600/50 hover:bg-violet-600" data-testid="add-members-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 p-4 glass-default rounded-xl border border-white/10 mb-4" data-testid="group-messages">
          {messages.map(msg => {
            const isMine = msg.sender_id === user?.user_id;
            return (
              <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] p-3 rounded-xl ${isMine ? 'bg-violet-600/40 rounded-br-sm' : 'bg-white/5 rounded-bl-sm'}`}>
                  {!isMine && <p className="text-xs font-medium text-violet-300 mb-1">{msg.sender_name}</p>}
                  <p className="text-white text-sm">{msg.content}</p>
                  <p className="text-white/30 text-xs mt-1">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            data-testid="group-message-input"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-white/5 border-white/10 text-white"
          />
          <Button type="submit" disabled={!newMessage.trim()} className="bg-violet-600 hover:bg-violet-500" data-testid="send-group-msg-btn">
            <Send className="w-4 h-4" />
          </Button>
        </form>

        <AddMembersModal
          open={showAddMembers}
          onClose={() => { setShowAddMembers(false); fetchGroupChat(groupId); }}
          groupId={groupId}
          currentMembers={members.map(m => m.user_id)}
        />
      </div>
    </div>
  );
}
